import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileCheck2, FilePenLine, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { getDocumentDetail } from '@/features/documents/api';
import { createAuthoringDraftFromUpload } from '../../api/authoringApi';

interface Props {
  source: { id: string; name: string };
  onEdit: (draftId: string) => void;
  onRegister: (id: string, name: string) => void;
}

export function UploadedDocumentChoice({ source, onEdit, onRegister }: Props) {
  const { t, i18n } = useTranslation('qms');
  const [waiting, setWaiting] = useState(false);
  const active = useRef(true);
  const opening = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const detail = useQuery({
    queryKey: ['eqms', 'upload-source', source.id],
    queryFn: () => getDocumentDetail(source.id),
    retry: false,
    refetchInterval: query => {
      const version = query.state.data?.data.currentVersion;
      return !query.state.error && version && version.status === 'pending' && version.conversionStatus !== 'failed' ? 2000 : false;
    },
  });
  const document = detail.data?.data;
  const version = document?.currentVersion;
  const name = document?.name || source.name;
  const isWord = version?.sourceMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || /\.docx$/i.test(version?.originalName || name);
  const extracted = version?.conversionStatus === 'done' || version?.conversionStatus === 'skipped'
    || (!version?.conversionStatus && version?.status === 'ready');
  const failed = !version || version.status === 'failed' || version.conversionStatus === 'failed';
  const importable = Boolean(version && (isWord || extracted));
  const create = useMutation({
    retry: false,
    mutationFn: () => createAuthoringDraftFromUpload(source.id, i18n.language.startsWith('fr') ? 'fr' : 'en'),
    onSuccess: ({ draft }) => { if (active.current) onEdit(draft.id); },
    onSettled: () => { opening.current = false; if (active.current) setWaiting(false); },
  });
  const { mutate } = create;
  useEffect(() => {
    if (waiting && importable && !opening.current) { opening.current = true; mutate(); }
    else if (waiting && document && failed && !isWord) setWaiting(false);
  }, [waiting, importable, document, failed, isWord, mutate]);
  const startEditing = () => {
    if (opening.current) return;
    if (!importable) { setWaiting(true); return; }
    opening.current = true; mutate();
  };
  const failureCode = create.error instanceof ApiError && typeof create.error.data === 'object' && create.error.data
    && 'code' in create.error.data ? String(create.error.data.code) : '';
  const failureKey = ['IMPORT_TOO_LARGE', 'IMPORT_TOO_COMPLEX', 'IMPORT_TABLE_TOO_WIDE', 'IMPORT_UNSUPPORTED_CONTENT'].includes(failureCode)
    ? 'importTooComplex' : failureCode === 'IMPORT_UNSUPPORTED_TYPE' ? 'importUnsupported'
      : ['IMPORT_EMPTY', 'IMPORT_UNREADABLE', 'IMPORT_SOURCE_UNAVAILABLE'].includes(failureCode) ? 'importUnavailable'
        : failureCode === 'IMPORT_SOURCE_PROCESSING' ? 'processing' : '';
  return <div className="space-y-4 py-2">
    {name && <p className="break-words rounded-lg border bg-slate-50 p-3 text-sm font-medium text-slate-900">{name}</p>}
    {detail.isLoading && <p role="status" className="text-sm text-slate-500">{t('authoring.loading')}</p>}
    {detail.isError && <div role="alert" className="space-y-2 text-sm text-red-700"><p>{detail.error.message}</p><Button variant="outline" onClick={() => { void detail.refetch(); }}>{t('authoring.retry')}</Button></div>}
    {document && failed && !isWord && <p role="alert" className="text-sm text-red-700">{t('authoring.importUnavailable')}</p>}
    {document && !failed && !importable && <p role="status" className="text-sm text-slate-600">{t(waiting ? 'authoring.processingAutoOpen' : 'authoring.processing')}</p>}
    {create.isError && <p role="alert" className="text-sm text-red-700">{failureKey ? t(`authoring.${failureKey}`) : create.error.message}</p>}
    <div className="grid gap-3 sm:grid-cols-2">
      <Button className="h-auto min-h-24 flex-col gap-2 whitespace-normal bg-teal-700 p-4 hover:bg-teal-800"
        disabled={!document || detail.isError || (failed && !isWord) || create.isPending || waiting} onClick={startEditing}>
        {create.isPending || waiting ? <Loader2 className="h-5 w-5 animate-spin" /> : <FilePenLine className="h-5 w-5" />}
        {t('authoring.editUploaded')}
      </Button>
      <Button variant="outline" className="h-auto min-h-24 flex-col gap-2 whitespace-normal p-4"
        disabled={!document || detail.isError || version?.status !== 'ready' || create.isPending || waiting}
        onClick={() => { if (document) onRegister(document.id, document.name); }}>
        <FileCheck2 className="h-5 w-5" />{t('authoring.registerAsIs')}
      </Button>
    </div>
    <p className="text-xs leading-5 text-slate-500">{t('authoring.importReviewHint')}</p>
  </div>;
}
